import type { BuilderAsset } from "./types";

const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "ogg", "m4a", "aac", "flac"]);

function extension(name: string): string {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

function safeFileName(name: string): string {
  const normalized = name.normalize("NFKD").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+/, "");
  return normalized && /^[A-Za-z0-9]/.test(normalized) ? normalized.slice(0, 255) : `asset.${extension(name) || "bin"}`;
}

function assetId(type: BuilderAsset["type"]): string {
  return `${type === "gltf" ? "model" : "audio"}_${crypto.randomUUID().replaceAll("-", "")}`;
}

async function validateGltfDependencies(file: File): Promise<void> {
  if (extension(file.name) !== "gltf") return;
  let parsed: { buffers?: { uri?: string }[]; images?: { uri?: string }[] };
  try { parsed = JSON.parse(await file.text()) as typeof parsed; }
  catch { throw new Error("This GLTF file is not valid JSON. Export it as GLB and try again."); }
  const uris = [...(parsed.buffers ?? []), ...(parsed.images ?? [])]
    .map((item) => item.uri)
    .filter((uri): uri is string => Boolean(uri));
  if (uris.some((uri) => !uri.startsWith("data:"))) {
    throw new Error("This GLTF references external files. Convert or export it as GLB for this version.");
  }
}

export async function prepareAsset(file: File, type: BuilderAsset["type"]): Promise<BuilderAsset> {
  const ext = extension(file.name);
  if (type === "gltf" && ext !== "glb" && ext !== "gltf") throw new Error("Choose a .glb or self-contained .gltf model.");
  if (type === "audio" && !AUDIO_EXTENSIONS.has(ext)) throw new Error("Choose a supported audio file: MP3, WAV, OGG, M4A, AAC, or FLAC.");
  if (file.size > 25 * 1024 * 1024) throw new Error("Each asset must be 25 MB or smaller for this staging version.");
  await validateGltfDependencies(file);
  return {
    id: assetId(type),
    type,
    fileName: safeFileName(file.name),
    displayName: file.name,
    ...(file.type ? { mediaType: file.type } : {}),
    file
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
